/**
 * Vote Resolution Engine — standalone utility for computing vote tallies
 * and checking resolution conditions. No React dependencies.
 */
import type {
  Vote,
  VoteOption,
  VoteResult,
  VoteResolution,
  VotingCardProperties,
} from '@/types/pm-types';

/**
 * Computes per-option vote tallies from a flat list of votes.
 */
export function computeVoteResults(
  votes: Vote[],
  options: VoteOption[]
): VoteResult[] {
  return options.map((option) => {
    const optionVotes = votes.filter((v) => v.optionId === option.id);
    const approveCount = optionVotes.filter((v) => v.decision === 'approve').length;
    const vetoCount = optionVotes.filter((v) => v.decision === 'veto').length;
    const abstainCount = optionVotes.filter((v) => v.decision === 'abstain').length;

    let status: VoteResult['status'] = 'pending';
    if (vetoCount > 0 && approveCount === 0) {
      status = 'vetoed';
    } else if (approveCount > 0 && vetoCount === 0) {
      status = 'approved';
    }

    return {
      optionId: option.id,
      approveCount,
      vetoCount,
      abstainCount,
      status,
    };
  });
}

/**
 * Checks whether resolution has been reached based on the resolution config.
 */
export function checkResolution(
  results: VoteResult[],
  resolution: VoteResolution,
  totalEligible: number,
  deadline?: string
): { resolved: boolean; winnerOptionId?: string; reason?: string } {
  if (totalEligible === 0) {
    return { resolved: false, reason: 'No eligible voters' };
  }

  const totalVotes = results.reduce(
    (sum, r) => sum + r.approveCount + r.vetoCount + r.abstainCount,
    0
  );
  // De-duplicate: for single-option voting, total = sum across options;
  // for multi-option voting (approve-veto), each voter may vote on multiple options.

  const isPastDeadline = deadline ? new Date() > new Date(deadline) : false;

  // Handle deadline timeout
  if (isPastDeadline && deadline) {
    if (resolution.timeoutAction === 'extend') {
      // Don't auto-resolve — just note the deadline passed
      const baseCheck = checkResolutionByType(results, resolution, totalEligible);
      if (baseCheck.resolved) return baseCheck;
      return { resolved: false, reason: 'Deadline passed, extended' };
    }

    // For approve-remaining / veto-remaining, synthesize missing votes
    const augmentedResults = results.map((r) => {
      const votedOnThis = r.approveCount + r.vetoCount + r.abstainCount;
      const remaining = Math.max(0, totalEligible - votedOnThis);
      if (resolution.timeoutAction === 'approve-remaining') {
        return { ...r, approveCount: r.approveCount + remaining };
      } else {
        return { ...r, vetoCount: r.vetoCount + remaining };
      }
    });
    return checkResolutionByType(augmentedResults, resolution, totalEligible);
  }

  return checkResolutionByType(results, resolution, totalEligible);
}

function checkResolutionByType(
  results: VoteResult[],
  resolution: VoteResolution,
  totalEligible: number
): { resolved: boolean; winnerOptionId?: string; reason?: string } {
  switch (resolution.type) {
    case 'unanimous': {
      // All eligible voters must have voted AND all approve on at least one option
      const allVoted = results.every((r) => {
        const total = r.approveCount + r.vetoCount + r.abstainCount;
        return total >= totalEligible;
      });
      if (!allVoted) return { resolved: false };

      const anyVetoed = results.some((r) => r.vetoCount > 0);
      if (anyVetoed) {
        return { resolved: true, reason: 'Unanimous resolution failed — vetoed' };
      }

      const allApproved = results.every((r) => r.approveCount === totalEligible);
      if (allApproved) {
        const winner = results.find((r) => r.approveCount === totalEligible);
        return {
          resolved: true,
          winnerOptionId: winner?.optionId,
          reason: `Unanimous approval (${totalEligible} of ${totalEligible})`,
        };
      }
      return { resolved: false };
    }

    case 'majority': {
      const threshold = Math.floor(totalEligible / 2) + 1;
      const winner = results.find((r) => r.approveCount >= threshold);
      if (winner) {
        return {
          resolved: true,
          winnerOptionId: winner.optionId,
          reason: `Majority reached (${winner.approveCount} of ${totalEligible})`,
        };
      }
      // Check if any option is vetoed beyond recovery
      const impossibleToWin = results.every((r) => {
        const remaining = totalEligible - (r.approveCount + r.vetoCount + r.abstainCount);
        return r.approveCount + remaining < threshold;
      });
      if (impossibleToWin) {
        return { resolved: true, reason: 'No option can reach majority' };
      }
      return { resolved: false };
    }

    case 'quorum': {
      const quorumTarget =
        resolution.quorumCount ??
        Math.ceil((resolution.quorumPercentage ?? 50) / 100 * totalEligible);
      const uniqueVoterIds = new Set<string>();
      // Since we only have counts, use the max votes on any option as a proxy
      const maxVotesOnAny = Math.max(
        ...results.map((r) => r.approveCount + r.vetoCount + r.abstainCount),
        0
      );
      if (maxVotesOnAny >= quorumTarget) {
        const winner = results.reduce((best, r) =>
          r.approveCount > best.approveCount ? r : best
        );
        return {
          resolved: true,
          winnerOptionId: winner.optionId,
          reason: `Quorum reached (${maxVotesOnAny} of ${quorumTarget} required)`,
        };
      }
      return { resolved: false };
    }

    case 'any': {
      const hasAnyVote = results.some(
        (r) => r.approveCount > 0 || r.vetoCount > 0
      );
      if (hasAnyVote) {
        const winner = results.reduce((best, r) =>
          r.approveCount > best.approveCount ? r : best
        );
        return {
          resolved: true,
          winnerOptionId: winner.optionId,
          reason: 'At least one vote cast',
        };
      }
      return { resolved: false };
    }

    default:
      return { resolved: false };
  }
}

/**
 * For single-choice: returns the option with the most votes.
 * For ranked: computes Borda count from ranked votes.
 */
export function computeWinner(
  votes: Vote[],
  options: VoteOption[],
  voteType: VotingCardProperties['voteType']
): string | undefined {
  if (options.length === 0 || votes.length === 0) return undefined;

  if (voteType === 'ranked') {
    const rankedVotes = votes
      .filter((v) => v.decision === 'approve' && (v as any).rankedOptionIds)
      .map((v) => ({
        voterId: v.voterId,
        rankedOptionIds: (v as any).rankedOptionIds as string[],
      }));
    const scores = computeBordaCount(rankedVotes, options);
    const entries = Object.entries(scores);
    if (entries.length === 0) return undefined;
    entries.sort(([, a], [, b]) => b - a);
    return entries[0][0];
  }

  // For single-choice, approve-veto, thumbs: most approvals wins
  const results = computeVoteResults(votes, options);
  const sorted = [...results].sort((a, b) => b.approveCount - a.approveCount);

  // Tie check
  if (
    sorted.length > 1 &&
    sorted[0].approveCount === sorted[1].approveCount
  ) {
    return undefined; // tie
  }

  return sorted[0]?.optionId;
}

/**
 * Computes Borda count scores for ranked voting.
 * Each voter's #1 pick gets N points, #2 gets N-1, etc.
 */
export function computeBordaCount(
  rankedVotes: Array<{ voterId: string; rankedOptionIds: string[] }>,
  options: VoteOption[]
): Record<string, number> {
  const n = options.length;
  const scores: Record<string, number> = {};
  options.forEach((o) => (scores[o.id] = 0));

  for (const vote of rankedVotes) {
    vote.rankedOptionIds.forEach((optionId, index) => {
      if (scores[optionId] !== undefined) {
        scores[optionId] += n - index;
      }
    });
  }

  return scores;
}
